import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ChatMessage from './ChatMessage';
import SampleRequestMode from './modes/SampleRequestMode';
import { useChatSession } from './hooks/useChatSession';
import { useSampleRequestForm } from './hooks/useSampleRequestForm';
import { CHAT_DRAFT_KEY } from './chat.types';

interface ChatPanelProps {
  onClose: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = memo(({ onClose }) => {
  // 마지막 AI 메시지 typing 애니메이션 진행 여부 (FAQ 컨테이너 미노출 조건)
  const [isAnimatingResponse, setIsAnimatingResponse] = useState(false);

  const chat = useChatSession({
    onSendMessageStart: () => setIsAnimatingResponse(true),
  });
  const sampleForm = useSampleRequestForm({ onClose });

  const handleTypingChange = useCallback((isTyping: boolean) => {
    setIsAnimatingResponse(isTyping);
  }, []);

  // 저장된 sample request draft 가 있으면 mount 직후 1회 복구 (재인증 후 자동 복귀)
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    if (chat.showSampleRequestGuide) return;
    const saved = sessionStorage.getItem(CHAT_DRAFT_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed.formData && Object.keys(parsed.formData).length > 0) {
        chat.handleStartSampleRequest();
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sample request 모드 진입 시 폼 초기화
  useEffect(() => {
    if (chat.showSampleRequestGuide) {
      sampleForm.initializeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.showSampleRequestGuide]);

  const handleNewChat = () => {
    chat.handleNewChat();
    sampleForm.resetForm();
  };

  const handleStartSampleRequest = () => {
    chat.handleStartSampleRequest();
    sampleForm.initializeForm();
  };

  const handleCancelSampleRequest = () => {
    chat.setShowSampleRequestGuide(false);
    sampleForm.setShowSuccessScreen(false);
    chat.setChatMode('initial');
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden" style={{ overscrollBehavior: 'contain' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-np-600 text-white">
        <div className="flex items-center gap-2">
          <button
            onClick={() => chat.setShowSessions(!chat.showSessions)}
            className="p-1 hover:bg-np-700 rounded"
            title="Chat history"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h3 className="font-medium text-sm">NXP AI Assistant</h3>
        </div>
        <div className="flex items-center gap-2">
          {!chat.showSampleRequestGuide && !sampleForm.showSuccessScreen && (
            <button
              onClick={handleStartSampleRequest}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-full transition-colors shadow-sm"
              title="Request a Sample"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Request a Sample</span>
            </button>
          )}
          <button
            onClick={handleNewChat}
            className="p-1 hover:bg-np-700 rounded"
            title="New chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-np-700 rounded"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Sessions sidebar */}
      {chat.showSessions && (
        <div className="absolute top-12 left-0 right-0 bottom-0 bg-white z-10 overflow-y-auto border-t border-gray-200">
          <div className="p-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Chat History</h4>
            {chat.sessions.length === 0 ? (
              <p className="text-sm text-gray-500">No chat history yet</p>
            ) : (
              <div className="space-y-1">
                {chat.sessions.map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => chat.handleSelectSession(session.session_id)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      chat.currentSessionId === session.session_id
                        ? 'bg-np-50 text-np-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="truncate font-medium">{session.title || 'New Chat'}</div>
                    <div className="text-xs text-gray-400">
                      {session.message_count} messages
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" style={{ overscrollBehavior: 'contain' }}>
        {/* Sample Request Mode */}
        {chat.showSampleRequestGuide && (
          <SampleRequestMode
            onClose={onClose}
            onCancel={handleCancelSampleRequest}
            onNewRequest={handleNewChat}
          />
        )}

        {/* Initial Screen - Welcome message */}
        {chat.chatMode === 'initial' && !chat.showSampleRequestGuide && chat.faqMessages.length === 0 && (
          <div className="py-4 text-center">
            <div className="w-12 h-12 bg-np-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-np-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">How can I help you?</h3>
            <p className="text-sm text-gray-500 mb-4">Ask me anything about our products and services</p>
          </div>
        )}

        {/* Messages — FAQ + API merged by timestamp */}
        {chat.messagesLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 border-2 border-np-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          (() => {
            // Merge FAQ and API messages, sort by created_at
            const faq = chat.faqMessages.map(m => ({
              key: `faq-${m.id}`,
              role: m.role as 'user' | 'assistant',
              content: m.content,
              timestamp: m.created_at,
              animate: m.animate,
              sources: undefined,
            }));
            const api = chat.messages.map(m => ({
              key: `msg-${m.id}`,
              role: m.role,
              content: m.content,
              timestamp: m.created_at,
              animate: m.role === 'assistant' && m.id === chat.lastAiMessageId,
              sources: m.sources,
            }));
            const merged = [...faq, ...api].sort(
              (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            return merged.map(m => (
              <ChatMessage
                key={m.key}
                role={m.role}
                content={m.content}
                timestamp={m.timestamp}
                animate={m.animate}
                sources={m.sources}
                onTypingChange={m.animate ? handleTypingChange : undefined}
              />
            ));
          })()
        )}

        {chat.sendMessageMutation.isPending && (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs">AI is thinking...</span>
          </div>
        )}

        {/* FAQ Categories */}
        {chat.chatMode === 'initial' && !chat.showSampleRequestGuide && !chat.sendMessageMutation.isPending && !isAnimatingResponse && (
          (chat.faqFlowStep === 'categories' || chat.faqFlowStep === 'answer') && chat.faqCategories.length > 0 && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 mb-3">
                {chat.faqFlowStep === 'answer' ? 'More questions?' : 'Frequently Asked Questions'}
              </p>
              <div className="space-y-2">
                {chat.faqCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => chat.handleFaqCategoryClick(category)}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 bg-gray-50 hover:bg-np-50 hover:text-np-700 rounded-lg border border-gray-200 hover:border-np-300 transition-colors"
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        {/* FAQ Items */}
        {chat.chatMode === 'initial' && chat.faqFlowStep === 'items' && chat.selectedFaqCategory && !chat.showSampleRequestGuide && !isAnimatingResponse && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500">Select a question:</p>
              <button
                onClick={chat.handleBackToCategories}
                className="text-xs text-np-600 hover:text-np-700 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            </div>
            <div className="space-y-2">
              {chat.selectedFaqCategory.items?.map((item) => (
                <button
                  key={item.id}
                  onClick={() => chat.handleFaqItemClick(item)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 bg-gray-50 hover:bg-np-50 hover:text-np-700 rounded-lg border border-gray-200 hover:border-np-300 transition-colors"
                >
                  {item.question}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={chat.messagesEndRef} />
      </div>

      {/* Input */}
      {(chat.chatMode === 'initial' || chat.chatMode === 'question') && !sampleForm.showSuccessScreen && !chat.showSampleRequestGuide && (
        <form onSubmit={chat.handleSubmit} className="p-3 bg-white border-t border-gray-200">
          <div className="flex items-end gap-2">
            <textarea
              ref={chat.inputRef}
              value={chat.message}
              onChange={(e) => {
                chat.setMessage(e.target.value);
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              }}
              onKeyDown={chat.handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md resize-none focus:ring-1 focus:ring-np-500 focus:border-np-500"
              style={{ minHeight: '36px', maxHeight: '120px' }}
              disabled={chat.sendMessageMutation.isPending}
            />
            <button
              type="submit"
              disabled={!chat.message.trim() || chat.sendMessageMutation.isPending}
              className="px-3 py-2 bg-np-600 text-white rounded-md hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </form>
      )}
    </div>
  );
});

ChatPanel.displayName = 'ChatPanel';

export default ChatPanel;
